import { useEffect, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";
import RelatedStudents from "./components/RelatedStudents";
import { useAuth } from "@/hooks/useAuth";
import { Student } from "@/types";
import { AuthAlerts } from "./components/Alerts";
import { useAppDispatch } from "@/app/hooks";
import { getStudentByIdentifier, updateUser } from "@/features/users/usersThunks";
import { roleMap } from "@/utils/permissions";


const UserProfile = () => {
  const dispatch = useAppDispatch();
  const [students, setStudents] = useState<Student[]>([]);
  const [showRelatedStudents, setShowRelatedStudents] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState<"error" | "info" | "success">("success");
  const [alertMessage, setAlertMessage] = useState("");
  const [studentsChanged, setStudentsChanged] = useState(false);
  const { authUser, currentUser } = useAuth();


  useEffect(() => {
    if (authUser && currentUser && authUser.role === "attendant") {
      setStudents(currentUser.relatedStudents || []);
      setShowRelatedStudents(true);
    }
  }, [currentUser, authUser])

  useEffect(() => {
    const updateRelatedStudents = async () => {
      if (studentsChanged && currentUser) {
        try {
          const userToUpdate = {...currentUser, relatedStudents: students};
          await dispatch(updateUser({ id: currentUser.id! , data: userToUpdate, role: roleMap[authUser?.role as string] })).unwrap();
          setStudentsChanged(false);
        } catch (error) {
          setAlertMessage("Error al actualizar los estudiantes asociados");
          setAlertSeverity("error");
          setAlertOpen(true);
        }
      }
    };

    updateRelatedStudents();
  }, [studentsChanged, dispatch, currentUser, students]);

  const handleAddStudent = async (newStudent: Student) => {

    const student = await dispatch(getStudentByIdentifier(newStudent.id)).unwrap();
    if (student) {
      const exist = students.find((student) => Number(student.id) === Number(newStudent.id));
      if (exist) {
        setAlertMessage("El estudiante ya esta asociado");
        setAlertSeverity("error");
        setAlertOpen(true);
        return
      }
      setStudents((prevStudents) => [...prevStudents, {
        id: student.identifier,
        name: student.displayName,
        documentId: student.identifier
      }]);

      setStudentsChanged(true);
    }

  };

  const handleDeleteStudent = (id: string) => {
    if (students.length === 1) {
      setAlertMessage("Debe tener al menos un estudiante asociado");
      setAlertSeverity("error");
      setAlertOpen(true);
      setStudentsChanged(false);
      return
    }
    setStudentsChanged(true);
    setStudents((prevStudents) => prevStudents.filter((student) => student.id !== id));
  };

  return (
    <Box
      sx={{
        mt: { xs: 2, sm: 4, md: 6 },
        px: { xs: 2, sm: 4, md: 8, lg: 18 },
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 3, md: 4 },
        alignItems: "flex-start",
        justifyContent: "center",
        pb:8
      }}
    >
      {/* Imagen de perfil */}
      <Box
        sx={{
          width: { xs: "100%", md: "35%" },
        }}
      >
        <Paper
          elevation={2}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        >
          <Box
            component="img"
            src="/images/img12.jpg"
            alt="Profile"
            sx={{
              width: "100%",
              height: { xs: "250px", sm: "300px", md: "400px" },
              display: "block",
              objectFit: "cover",
            }}
          />
        </Paper>
      </Box>

      {/* Información del usuario */}
      <Box sx={{ width: { xs: "100%", md: "65%" } }}>
        {/* Nombre */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: "secondary.main",
            p: { xs: 2, sm: 2.5 },
            borderRadius: "12px",
            mb: { xs: 2, md: 3 },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#fff",
              fontWeight: 600,
              textAlign: "center",
              fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
              letterSpacing: "1px",
            }}
          >
            {currentUser?.displayName.toLocaleUpperCase()}
          </Typography>
        </Paper>

        {/* Detalles */}
        <Paper
          elevation={1}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          {/* Fila 1: ID y Tipo de ID */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: { xs: 2, sm: 3 },
              mb: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Check
                sx={{
                  color: "success.main",
                  fontSize: 22,
                  mr: 2,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: "#555",
                  fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                  wordBreak: "break-word",
                }}
              >
                {currentUser?.identifier}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Check
                sx={{
                  color: "success.main",
                  fontSize: 22,
                  mr: 2,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: "#555",
                  fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                  wordBreak: "break-word",
                }}
              >
                {currentUser?.identifierType}
              </Typography>
            </Box>
          </Box>

          {/* Fila 2: Email y Extensión */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: { xs: 2, sm: 3 },
              mb: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Check
                sx={{
                  color: "success.main",
                  fontSize: 22,
                  mr: 2,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: "#555",
                  fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                  wordBreak: "break-word",
                }}
              >
                {currentUser?.email}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Check
                sx={{
                  color: "success.main",
                  fontSize: 22,
                  mr: 2,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: "#555",
                  fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                  wordBreak: "break-word",
                }}
              >
                {currentUser?.phoneNumber || "Celular no registrado"}
              </Typography>
            </Box>
          </Box>

          {/* Fila 3: Rol y Sede */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: { xs: 2, sm: 3 },
              mb: { xs: 3, md: 4 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Check
                sx={{
                  color: "success.main",
                  fontSize: 22,
                  mr: 2,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: "#555",
                  fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                  wordBreak: "break-word",
                }}
              >
                {authUser?.role}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Check
                sx={{
                  color: "success.main",
                  fontSize: 22,
                  mr: 2,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: "#555",
                  fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                  wordBreak: "break-word",
                }}
              >
                {currentUser?.site || "Sede no registrada"}
              </Typography>
            </Box>
          </Box>

          {/* Contraseña */}
          {/* <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 1.5, sm: 2 },
              mb: showRelatedStudents ? { xs: 3, md: 4 } : 0
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Check
                sx={{
                  color: "success.main",
                  fontSize: 22,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: "#555",
                  fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                }}
              >
                ¿Quiere cambiar su contraseña?
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="medium"
              sx={{
                backgroundColor: "#9e9e9e",
                color: "#fff",
                textTransform: "none",
                px: { xs: 2, sm: 3 },
                py: 1,
                fontSize: { xs: "0.85rem", sm: "0.9rem" },
                fontWeight: 600,
                minWidth: { xs: "80px", sm: "100px" },
                height: "30px",
                borderRadius: "6px",
                alignSelf: { xs: "flex-start", sm: "auto" },
                ml: { xs: 0, sm: "auto" },
                "&:hover": {
                  backgroundColor: "#757575",
                },
              }}
            >
              Cambiar
            </Button>
          </Box> */}

          {/* Estudiantes Relacionados - Dentro del mismo Paper */}
          {showRelatedStudents && currentUser?.relatedStudents && (
            <RelatedStudents
              students={students}
              handleAddStudent={handleAddStudent}
              handleDeleteStudent={handleDeleteStudent}
            />
          )}
        </Paper>
      </Box>

      <AuthAlerts alertOpen={alertOpen} setAlertOpen={setAlertOpen} alertSeverity={alertSeverity} alertMessage={alertMessage} />
    </Box>
  );
};

export default UserProfile;